import { CameraContext } from "@/contexts/CameraContext";
import { ScrollAnimContext } from "@/contexts/ScrollAnimContext";
import { render, type WorldNode } from "@/libs/20thCenturyFox";
import cx from "classnames";
import { useTransform, motion, type MotionStyle } from "motion/react";
import { useContext, useEffect, useState } from "react";

export default function ShowcaseItem({
  children,
  className,
  active,
  node,
  ref,
  style,
}: {
  children?: React.ReactNode;
  className?: string;
  active?: boolean;
  node: WorldNode;
  ref?: React.Ref<HTMLDivElement>;
  style?: MotionStyle;
}) {
  const calculateSize = () => {
    // Determine the height and width based on the viewport size
    const result = { height: "25svw", width: "30svw" }; // Default values
    const windowWidth = window.innerWidth;

    // Height
    if (windowWidth >= 1536) {
      result.height = "13svw";
    } else if (windowWidth >= 768) {
      result.height = "15svw";
    }

    // Width
    if (windowWidth >= 1536) {
      result.width = "18svw";
    } else if (windowWidth >= 768) {
      result.width = "20svw";
    }

    return result;
  };

  // Determine the starting height and width to use based on the viewport
  const [startHeight, setStartHeight] = useState(() => calculateSize().height);
  const [startWidth, setStartWidth] = useState(() => calculateSize().width);

  // Animate the out transition
  const scrollYProgress = useContext(ScrollAnimContext);

  // Collapse visually into a dot
  const borderColor = useTransform(scrollYProgress, [0.5, 1], ["rgba(153, 161, 175, 1)", "rgba(153, 161, 175, 0)"]);
  const borderRadius = useTransform(scrollYProgress, [0, 1], ["0.5rem", "10rem"]);
  const backgroundColor = useTransform(scrollYProgress, [0.5, 1], ["rgba(0, 0, 0, 0.5)", "rgba(255, 255, 255, 1)"]);

  const varHeight = useTransform(scrollYProgress, [0, 1], [startHeight, "1svw"]);
  const varWidth = useTransform(scrollYProgress, [0, 1], [startWidth, "1svw"]);

  // Render position based on camera
  const camera = useContext(CameraContext);
  const { x, y } = render(node, camera, { height: varHeight.get(), width: varWidth.get() });

  // Recalculate size on breakpoint change
  useEffect(() => {
    const handleResize = () => {
      const { height, width } = calculateSize();
      setStartHeight(height);
      setStartWidth(width);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <motion.div
      ref={ref}
      className={cx("border-dashed border-2 transition-shadow bg-black duration-300 absolute shadow-2xl", className, {
        "opacity-100 shadow-white/30": active,
      })}
      style={{
        borderColor,
        borderRadius,
        backgroundColor,
        left: x,
        top: y,
        // Scale down spawned nodes to the zoomed out size
        // We have to combine the translate and scale transforms because the stylings conflict if we try to set them separately
        transform: `translate(-50%, -50%) ${node.kind === "spawned" ? "scale(0.2)" : ""}`,
        height: varHeight,
        width: varWidth,
        ...style,
      }}
    >
      {children}
    </motion.div>
  );
}
